import React, { useState } from 'react';
import { Mail, Lock, Eye, EyeOff, ArrowLeft } from 'lucide-react';
import { Button, Input, GardenBackground } from '../components/ui';
import { authService } from '../services/authService';

export const GuardianLogin = ({ onNavigate, onLogin, mockUsers }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    const newErrors = {};

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      setIsSubmitting(true);

      try {
        const loginResult = await authService.login(formData.email, formData.password);

        if (loginResult.success) {
          onLogin(loginResult.data);
          return;
        }
      } catch (error) {
        setErrors({
          email: 'Invalid email or password. Try demo accounts or create a new family.'
        });
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSubmit();
    }
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center px-4 py-12">
      <GardenBackground />
      <div className="w-full max-w-sm relative z-10">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          {/* Header */}
          <div className="text-center mb-6">
            <h2 className="text-2xl text-gray-900 mb-1" style={{ fontFamily: "'DM Serif Display', serif" }}>Welcome Back!</h2>
            <p className="text-sm text-gray-600">Sign in to manage your family's savings</p>
          </div>

          {errors.general && (
            <div className="mb-4 p-3 bg-red-50 border border-red-100 rounded-xl">
              <p className="text-red-600 text-sm">{errors.general}</p>
            </div>
          )}

          <div className="space-y-4">
            <Input
              label="Email Address"
              type="email"
              icon={Mail}
              value={formData.email}
              onChange={(e) => {
                setFormData({ ...formData, email: e.target.value });
                setErrors({});
              }}
              onKeyPress={handleKeyPress}
              error={errors.email}
              placeholder="parent@example.com"
              disabled={isSubmitting}
            />

            <div className="relative">
              <Input
                label="Password"
                type={showPassword ? 'text' : 'password'}
                icon={Lock}
                value={formData.password}
                onChange={(e) => {
                  setFormData({ ...formData, password: e.target.value });
                  setErrors({});
                }}
                onKeyPress={handleKeyPress}
                error={errors.password}
                placeholder="Enter your password"
                disabled={isSubmitting}
              />
              <button
                type="button"
                className={`absolute right-3 top-[38px] text-gray-400 hover:text-gray-600 ${
                  isSubmitting ? 'pointer-events-none' : ''
                }`}
                onClick={() => setShowPassword(!showPassword)}
                disabled={isSubmitting}
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>

            <div className="pt-2 space-y-3">
              <Button
                onClick={handleSubmit}
                size="lg"
                className="w-full"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Signing In...' : 'Sign In'}
              </Button>

              <div className="text-center">
                <button
                  type="button"
                  className="text-sm text-emerald-600 hover:text-emerald-700"
                  onClick={() => onNavigate('createFamily')}
                  disabled={isSubmitting}
                >
                  Don't have an account? Create a family
                </button>
              </div>

              <button
                type="button"
                className="w-full text-sm text-gray-400 hover:text-gray-600 transition-colors inline-flex items-center justify-center gap-1 pt-1"
                onClick={() => onNavigate('welcome')}
                disabled={isSubmitting}
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                Back
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};